import { useState } from "react";
import { api, ApiError } from "../api";
import { DataTable } from "./DataTable";

type FieldType = "string" | "number" | "boolean" | "url" | "date";
type SourceType = "html" | "feed" | "json" | "sitemap" | "csv";
interface FieldRow {
  name: string;
  type: FieldType;
  required: boolean;
  /** JSON source only: the dot-path to read this field from each item (e.g. `price.amount`). */
  path?: string;
}

interface AssistResponse {
  extraction_config: unknown;
  valid: boolean;
  itemCount: number;
  nullRate: number;
  fieldErrors: string[];
  preview: unknown;
  /** The fetch path that actually worked — "js" when a static request had to escalate past a wall. */
  renderUsed?: "static" | "js";
  /** Present only for a FEED source: the canonical schema the server persisted (no client-built schema). */
  output_schema?: unknown;
  /** Set when assist sniffed a non-HTML body on the selector path — the UI offers a source switch. */
  detected_source?: "feed" | "json" | "csv";
  /** Human hint accompanying `detected_source`. */
  message?: string;
  /** Absent for a feed source (no model call). */
  usage?: { inputTokens: number; outputTokens: number; model: string };
}

const TYPES: FieldType[] = ["string", "number", "boolean", "url", "date"];

/** Friendly schedule presets -> cron, so non-technical users don't have to write cron. */
export const SCHEDULES: { label: string; cron: string }[] = [
  { label: "Manual only", cron: "" },
  { label: "Every hour", cron: "0 * * * *" },
  { label: "Every 6 hours", cron: "0 */6 * * *" },
  { label: "Every 12 hours", cron: "0 */12 * * *" },
  { label: "Daily (midnight)", cron: "0 0 * * *" },
  { label: "Weekly (Sun)", cron: "0 0 * * 0" },
  { label: "Custom cron…", cron: "__custom__" },
];

function errMsg(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 503) return "AI assist is unavailable — no Anthropic key configured on the server.";
    if (e.status === 502) return "Could not fetch the page or reach the model (502).";
    // Surface the server's SPECIFIC 422 reason (invalid URL, anti-bot block, or unusable model output)
    // rather than one catch-all line — the old hardcoded "model didn't return a usable extractor" was
    // misleading when the real cause was a bad URL or an anti-bot-protected page.
    if (e.status === 422)
      return (
        e.message ||
        "Couldn't build an extractor for this page — check the URL (include https://) and the fields, or try JS render mode."
      );
    return `Error ${e.status}: ${e.message}`;
  }
  return e instanceof Error ? e.message : String(e);
}

/** Pre-extraction interaction step types (mirrors the server ActionStep union; the API re-validates). */
type ActionType = "waitFor" | "click" | "fill" | "select" | "press" | "scroll" | "loadMore" | "waitMs";
const ACTION_TYPES: ActionType[] = ["waitFor", "click", "fill", "select", "press", "scroll", "loadMore", "waitMs"];
const ACTION_NEEDS_SELECTOR = new Set<ActionType>(["waitFor", "click", "fill", "select", "press", "loadMore"]);
/** One editor row; `num` is the single numeric input reused per type (times / maxClicks / ms). */
interface ActionRow {
  type: ActionType;
  selector: string;
  value: string;
  key: string;
  num: string;
  optional: boolean;
}
const newActionRow = (): ActionRow => ({ type: "click", selector: "", value: "", key: "", num: "", optional: false });

export function NewScraper({ onCreated, operator = false }: { onCreated: () => void; operator?: boolean }) {
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [isList, setIsList] = useState(false);
  const [sourceType, setSourceType] = useState<SourceType>("html");
  const feedMode = sourceType === "feed";
  const jsonMode = sourceType === "json";
  const htmlMode = sourceType === "html";
  const sitemapMode = sourceType === "sitemap";
  const csvMode = sourceType === "csv";
  const [feedMax, setFeedMax] = useState(""); // maxItems for feed, json AND csv
  const [rootPath, setRootPath] = useState(""); // json: dot-path to the item array
  const [samplePageUrl, setSamplePageUrl] = useState(""); // sitemap: a representative page to design selectors on
  const [siteMax, setSiteMax] = useState(""); // sitemap: maxUrls (pages per run)
  const [csvDelimiter, setCsvDelimiter] = useState(","); // csv: "," or "\t" (TSV)
  const [csvHasHeader, setCsvHasHeader] = useState(true); // csv: first row is a header row
  const [renderJs, setRenderJs] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [schedule, setSchedule] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [notifyOnChange, setNotifyOnChange] = useState(false);
  const [folder, setFolder] = useState("");
  const [rps, setRps] = useState("");
  const [extraUrls, setExtraUrls] = useState("");
  const [autopaginate, setAutopaginate] = useState(false);
  const [paginate, setPaginate] = useState(false);
  const [pageMode, setPageMode] = useState<"selector" | "param">("selector"); // next-link vs URL ?page=N
  const [nextSelector, setNextSelector] = useState("");
  const [pageParam, setPageParam] = useState("page");
  const [pageDedupe, setPageDedupe] = useState(false);
  const [maxPages, setMaxPages] = useState(5);
  const [detailMode, setDetailMode] = useState(false);
  const [detailUrlField, setDetailUrlField] = useState("");
  const [detailMaxItems, setDetailMaxItems] = useState(50);
  const [detailFields, setDetailFields] = useState<{ name: string; selector: string }[]>([
    { name: "", selector: "" },
  ]);
  const [fields, setFields] = useState<FieldRow[]>([{ name: "title", type: "string", required: true }]);
  const [actions, setActions] = useState<ActionRow[]>([]);
  const [result, setResult] = useState<AssistResponse | null>(null);
  const [busy, setBusy] = useState<"assist" | "save" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [advanced, setAdvanced] = useState(false);
  // Per-scraper request auth (applies to every source type): custom headers + a raw Cookie value. Lets a
  // scraper read auth-gated pages and reuse a human-solved cf_clearance cookie. Secrets — never exported.
  const [authOpen, setAuthOpen] = useState(false);
  const [authCookies, setAuthCookies] = useState("");
  const [authHeaders, setAuthHeaders] = useState<{ key: string; value: string }[]>([]);
  const [showRaw, setShowRaw] = useState(false);
  const [saved, setSaved] = useState(false);
  const [customCron, setCustomCron] = useState(false);
  // Operator-only (N6): publish the AI-built config straight to the shared Templates gallery — no
  // throwaway scraper. Optional "test instance" spins up a real scraper in the operator's own workspace
  // (for heal testing) after the template is created.
  const [pubOpen, setPubOpen] = useState(false);
  const [tplName, setTplName] = useState("");
  const [tplDesc, setTplDesc] = useState("");
  const [testInstance, setTestInstance] = useState(false);
  const [published, setPublished] = useState<{ name: string; slug: string; instance: boolean } | null>(null);

  function patchField(i: number, patch: Partial<FieldRow>) {
    setFields((fs) => fs.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  }

  function patchAction(i: number, patch: Partial<ActionRow>) {
    setActions((xs) => xs.map((a, idx) => (idx === i ? { ...a, ...patch } : a)));
  }

  /** Editor rows → the server's ActionStep objects, dropping incomplete steps + empty numeric knobs.
   *  The API re-validates against the ActionStep schema, so this only needs to be well-formed-ish. */
  function cleanActions(): Record<string, unknown>[] {
    return actions
      .map((a): Record<string, unknown> | null => {
        const sel = a.selector.trim();
        const num = a.num.trim() === "" ? undefined : Number(a.num);
        const opt = a.optional ? { optional: true } : {};
        switch (a.type) {
          case "waitFor":
            return sel ? { type: "waitFor", selector: sel, ...opt } : null;
          case "click":
            return sel ? { type: "click", selector: sel, ...opt } : null;
          case "fill":
            return sel && a.value ? { type: "fill", selector: sel, value: a.value } : null;
          case "select":
            return sel && a.value ? { type: "select", selector: sel, value: a.value } : null;
          case "press":
            return sel && a.key.trim() ? { type: "press", selector: sel, key: a.key.trim() } : null;
          case "scroll":
            return { type: "scroll", ...(num ? { times: num } : {}) };
          case "loadMore":
            return sel ? { type: "loadMore", selector: sel, ...(num ? { maxClicks: num } : {}) } : null;
          case "waitMs":
            return num != null && Number.isFinite(num) ? { type: "waitMs", ms: num } : null;
          default:
            return null;
        }
      })
      .filter((x): x is Record<string, unknown> => x !== null);
  }

  // includeDetail=false for the assist call (detail fields live on detail pages, not the list
  // page the AI sees); true on save so validation covers the merged detail fields (optional).
  function outputSchema(includeDetail = false) {
    const base = fields
      .filter((f) => f.name.trim())
      .map((f) => ({ name: f.name.trim(), type: f.type, required: f.required, array: false }));
    const detailOut =
      includeDetail && detailMode
        ? detailFields
            .filter((f) => f.name.trim())
            .map((f) => ({ name: f.name.trim(), type: "string" as const, required: false, array: false }))
        : [];
    return { isList, fields: [...base, ...detailOut] };
  }

  // JSON source: field rows → extraction fields whose `selector` is the dot-path. Path defaults to the
  // field name (the common "top-level key" case); Zod fills selectorType/multiple defaults server-side.
  function cleanJsonFields() {
    return fields
      .filter((f) => f.name.trim())
      .map((f) => ({ name: f.name.trim(), selector: f.path?.trim() || f.name.trim() }));
  }

  async function build() {
    setBusy("assist");
    setError(null);
    setResult(null);
    // Design-time auth: send the same request block to the preview fetch, so an authenticated JSON API or a
    // session-gated static page can be previewed here (applied on the static path server-side).
    const requestBlock = buildRequestBlock();
    const authField = requestBlock ? { request: requestBlock } : {};
    try {
      // Feed source: NO AI, NO schema — the server parses the RSS/Atom feed and returns the items +
      // the canonical feed schema. Send only the url + optional maxItems.
      if (feedMode) {
        const max = feedMax.trim() === "" ? undefined : Number(feedMax);
        const res = await api<AssistResponse>("/extractors/assist", {
          method: "POST",
          body: JSON.stringify({
            url: url.trim(),
            feed: max !== undefined && Number.isFinite(max) && max > 0 ? { maxItems: max } : {},
            ...authField,
          }),
        });
        setResult(res);
        setNotice(null);
        return;
      }
      // CSV / TSV source: NO AI, NO schema authoring. Send the url + csv config; the server fetches, parses,
      // derives a lenient string schema from the header row, and previews the rows.
      if (csvMode) {
        const max = feedMax.trim() === "" ? undefined : Number(feedMax);
        const csv: Record<string, unknown> = {};
        if (csvDelimiter !== ",") csv.delimiter = csvDelimiter;
        if (!csvHasHeader) csv.hasHeader = false;
        if (max !== undefined && Number.isFinite(max) && max > 0) csv.maxItems = max;
        const res = await api<AssistResponse>("/extractors/assist", {
          method: "POST",
          body: JSON.stringify({ url: url.trim(), csv, ...authField }),
        });
        setResult(res);
        setNotice(null);
        return;
      }
      // JSON source: NO AI. Send the url + json config (rootPath/maxItems) + client-authored dot-path
      // fields + the output schema; the server fetches, runs the JSON extractor, and previews the items.
      if (jsonMode) {
        const max = feedMax.trim() === "" ? undefined : Number(feedMax);
        const json: Record<string, unknown> = {};
        if (rootPath.trim()) json.rootPath = rootPath.trim();
        if (max !== undefined && Number.isFinite(max) && max > 0) json.maxItems = max;
        // A rootPath resolves to an ARRAY → the output is a list, regardless of the (easily-forgotten)
        // list checkbox. Force isList so the schema matches the shape and doesn't silently fail validation.
        const schema = outputSchema();
        const res = await api<AssistResponse>("/extractors/assist", {
          method: "POST",
          body: JSON.stringify({
            url: url.trim(),
            json,
            fields: cleanJsonFields(),
            output_schema: rootPath.trim() ? { ...schema, isList: true } : schema,
            ...authField,
          }),
        });
        setResult(res);
        setNotice(null);
        return;
      }
      // Sitemap source: design the per-page extractor on a SAMPLE page via the normal selector assist
      // (object mode — one record per page; the crawl aggregates them into a list on save). The main URL
      // is the sitemap; we don't fetch it here, only the sample page.
      if (sitemapMode) {
        const res = await api<AssistResponse>("/extractors/assist", {
          method: "POST",
          body: JSON.stringify({
            url: samplePageUrl.trim(),
            output_schema: { ...outputSchema(), isList: false },
            render: renderJs ? "js" : "static",
            ...authField,
          }),
        });
        setResult(res);
        setNotice(null);
        return;
      }
      const acts = cleanActions();
      const res = await api<AssistResponse>("/extractors/assist", {
        method: "POST",
        body: JSON.stringify({
          url: url.trim(),
          output_schema: outputSchema(),
          render: renderJs ? "js" : "static",
          autopaginate: isList && autopaginate,
          ...(acts.length ? { actions: acts } : {}),
          ...authField,
        }),
      });
      setResult(res);
      // Assist sniffed a non-HTML body (JSON/feed) and short-circuited with a suggestion — no config to
      // process. Stop here; the result render shows the "switch source" nudge. (Also avoids reading
      // .pagination off a null extraction_config below.)
      if (res.detected_source) {
        setNotice(null);
        return;
      }
      // The page needed a real browser (a passive anti-bot wall blocked the static fetch, e.g. an
      // Anubis proof-of-work). Assist auto-escalated to JS render; flip the toggle so SAVE persists JS
      // too (a saved static config would fail on the wall on every run), and tell the user why.
      if (res.renderUsed === "js" && !renderJs) {
        setRenderJs(true);
        setNotice("This site blocked a plain fetch (anti-bot wall) — auto-switched to JS render, which got through. The saved scraper will use JS render.");
      } else {
        setNotice(null);
      }
      // AI-detected pagination pre-fills the (editable) manual fields + reveals them.
      const pag = (res.extraction_config as { pagination?: { nextSelector?: string; maxPages?: number } }).pagination;
      if (isList && autopaginate && pag?.nextSelector) {
        setPaginate(true);
        setNextSelector(pag.nextSelector);
        setMaxPages(pag.maxPages ?? 5);
        setAdvanced(true);
      }
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(null);
    }
  }

  // Fold the auth form into an ExtractionConfig `request` block ({ headers?, cookies? }), or undefined when
  // empty. A header with a blank NAME is dropped (a blank value is allowed — some flags are name-only).
  function buildRequestBlock(): Record<string, unknown> | undefined {
    const headers: Record<string, string> = {};
    for (const h of authHeaders) {
      const k = h.key.trim();
      if (k) headers[k] = h.value;
    }
    const cookies = authCookies.trim();
    const req: Record<string, unknown> = {};
    if (Object.keys(headers).length > 0) req.headers = headers;
    if (cookies) req.cookies = cookies;
    return Object.keys(req).length > 0 ? req : undefined;
  }

  // Assemble the scraper definition (a ScraperBundleBody) from the AI result + the form — shared by
  // "Save & run" (POST /scrapers) and the operator "Publish as template" (POST /templates { bundle }).
  function assembleBundleBody(): Record<string, unknown> {
    const extra = extraUrls
      .split(/[\n,]/)
      .map((u) => u.trim())
      .filter(Boolean);
    const requestBlock = buildRequestBlock();
    // Sitemap source: the extractor was designed on a sample page; persist it with the `sitemap` marker and
    // the SITEMAP url as the target. Per-page extraction is object mode; the crawl aggregates → isList.
    if (sitemapMode) {
      const max = siteMax.trim() === "" ? undefined : Number(siteMax);
      const extractionConfig: Record<string, unknown> = { ...(result!.extraction_config as Record<string, unknown>) };
      extractionConfig.sitemap = max !== undefined && Number.isFinite(max) && max > 0 ? { maxUrls: max } : {};
      // Whole-site crawl doesn't use these selector-flow bits.
      delete extractionConfig.pagination;
      delete extractionConfig.detail;
      delete extractionConfig.actions;
      if (requestBlock) extractionConfig.request = requestBlock;
      const body: Record<string, unknown> = {
        name: name.trim() || url.trim(),
        target_urls: [url.trim(), ...extra], // url = the sitemap URL
        fetch_mode: "static",
        output_schema: { ...outputSchema(), isList: true },
        extraction_config: extractionConfig,
      };
      if (schedule.trim()) body.schedule = schedule.trim();
      if (webhookUrl.trim()) body.webhook_url = webhookUrl.trim();
      if (notifyOnChange) body.notify_on_change = true;
      return body;
    }
    // Feed / JSON / CSV source: no CSS selectors, flows, or actions. Persist the config + schema the server
    // returned in the preview verbatim; always the static fetch path.
    if (feedMode || jsonMode || csvMode) {
      const feedConfig: Record<string, unknown> = { ...(result!.extraction_config as Record<string, unknown>) };
      if (requestBlock) feedConfig.request = requestBlock;
      const body: Record<string, unknown> = {
        name: name.trim() || url.trim(),
        target_urls: [url.trim(), ...extra],
        fetch_mode: "static",
        output_schema: result!.output_schema,
        extraction_config: feedConfig,
      };
      if (schedule.trim()) body.schedule = schedule.trim();
      if (webhookUrl.trim()) body.webhook_url = webhookUrl.trim();
      if (notifyOnChange) body.notify_on_change = true;
      return body;
    }
    // Merge pagination (flows) into the AI-generated config — list mode only. The manual fields (which
    // AI-detect pre-fills) are the single source of truth, so drop any AI-embedded pagination first —
    // otherwise clearing the toggle wouldn't actually remove it.
    const extractionConfig = { ...(result!.extraction_config as Record<string, unknown>) };
    delete extractionConfig.pagination;
    if (paginate && isList) {
      const dedupe = pageDedupe ? { dedupe: true } : {};
      if (pageMode === "param" && pageParam.trim()) {
        extractionConfig.pagination = { pageParam: pageParam.trim(), maxPages, ...dedupe };
      } else if (pageMode === "selector" && nextSelector.trim()) {
        extractionConfig.pagination = { nextSelector: nextSelector.trim(), maxPages, ...dedupe };
      }
    }
    // Interactions: the editor is the single source of truth (so edits after assist persist). Overwrite
    // whatever assist echoed back with the current rows.
    delete extractionConfig.actions;
    const acts = cleanActions();
    if (acts.length > 0) extractionConfig.actions = acts;
    const cleanDetailFields = detailFields.filter((f) => f.name.trim() && f.selector.trim());
    if (detailMode && isList && detailUrlField.trim() && cleanDetailFields.length > 0) {
      extractionConfig.detail = {
        urlField: detailUrlField.trim(),
        maxItems: detailMaxItems,
        fields: cleanDetailFields.map((f) => ({
          name: f.name.trim(),
          selector: f.selector.trim(),
          selectorType: "css",
          multiple: false,
        })),
      };
    }
    if (requestBlock) extractionConfig.request = requestBlock;
    const body: Record<string, unknown> = {
      name: name.trim() || url.trim(),
      target_urls: [url.trim(), ...extra],
      fetch_mode: renderJs ? "js" : "static",
      output_schema: outputSchema(true),
      extraction_config: extractionConfig,
    };
    if (schedule.trim()) body.schedule = schedule.trim();
    if (webhookUrl.trim()) body.webhook_url = webhookUrl.trim();
    if (notifyOnChange) body.notify_on_change = true;
    return body;
  }

  async function save() {
    if (!result) return;
    setBusy("save");
    setError(null);
    try {
      // folder + rate_limit_rps are scraper-local settings — NOT part of the portable bundle (which also
      // feeds template publish), so they're added onto the create body here, not into assembleBundleBody().
      const body = assembleBundleBody();
      const rpsNum = rps.trim() === "" ? null : Number(rps);
      if (rpsNum !== null && (!Number.isFinite(rpsNum) || rpsNum <= 0 || rpsNum > 20)) {
        setError("Rate limit must be a number between 0 and 20 requests/second");
        setBusy(null);
        return;
      }
      if (folder.trim()) body.folder = folder.trim();
      if (rpsNum !== null) body.rate_limit_rps = rpsNum;
      const created = await api<{ id: string }>("/scrapers", {
        method: "POST",
        body: JSON.stringify(body),
      });
      // Kick off a first run so data appears right away. Best-effort — the scraper is already
      // created, so a run hiccup shouldn't surface as a save failure.
      try {
        await api(`/scrapers/${created.id}/run`, { method: "POST" });
      } catch {
        /* scraper created; run can be triggered manually from the list */
      }
      setSaved(true);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(null);
    }
  }

  // Operator (N6): publish the AI-built config to the shared gallery as a template — no throwaway scraper.
  async function publishTemplate() {
    if (!result || !tplName.trim()) return;
    setBusy("save");
    setError(null);
    try {
      const bundle = assembleBundleBody();
      const tpl = await api<{ id: string; slug: string; name: string }>("/templates", {
        method: "POST",
        body: JSON.stringify({
          name: tplName.trim(),
          description: tplDesc.trim() || undefined,
          bundle,
        }),
      });
      let instance = false;
      if (testInstance) {
        // Best-effort heal-test instance in the operator's own workspace; the template already exists.
        try {
          await api(`/templates/${tpl.id}/instantiate`, { method: "POST", body: "{}" });
          instance = true;
        } catch {
          /* template published; the test instance can be created from the gallery later */
        }
      }
      setPublished({ name: tpl.name, slug: tpl.slug, instance });
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(null);
    }
  }

  // A feed source needs only a URL (no fields — the shape is fixed); selector/json/sitemap need ≥1 field.
  // Sitemap also needs a sample page URL to design the per-page extractor on.
  const canBuild =
    url.trim() !== "" &&
    (feedMode || csvMode || fields.some((f) => f.name.trim())) &&
    (!sitemapMode || samplePageUrl.trim() !== "") &&
    busy === null;

  if (published) {
    return (
      <div className="stack">
        <section className="card success-card">
          <h2>Template published</h2>
          <p>
            <strong>{published.name}</strong> is now in the shared <strong>Templates</strong> gallery (
            <span className="mono">{published.slug}</span>). Any workspace can instantiate it in one click.
            {published.instance && " A test instance was created in your workspace for heal testing."}
          </p>
          <button className="btn" onClick={onCreated}>
            Done &rarr;
          </button>
        </section>
      </div>
    );
  }

  if (saved) {
    return (
      <div className="stack">
        <section className="card success-card">
          <h2>Scraper created — first run started</h2>
          <p>
            Your rows will land under <strong>Fleet → Data</strong> in a few seconds. From here it
            runs on its own, and repairs itself when the site changes.
          </p>
          <button className="btn" onClick={onCreated}>
            View your scraper &rarr;
          </button>
        </section>
      </div>
    );
  }

  return (
    <div className="stack">
      <div className="section-head">
        <div>
          <h1>New scraper</h1>
          <span className="muted">Paste a URL and the fields you want — Claude writes the extractor.</span>
        </div>
      </div>

      {/* ---- Step 1: the essentials ---- */}
      <section className="card">
        <div className="step-row">
          <span className="step-n">1</span>
          <h3>What to scrape</h3>
        </div>
        <div className="form-grid mt">
          <label>{sitemapMode ? "Sitemap URL" : "URL"}</label>
          <input
            placeholder={sitemapMode ? "https://example.com/sitemap.xml" : "https://example.com/products"}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            autoFocus
          />

          <label>Name</label>
          <input placeholder="optional — defaults to the URL" value={name} onChange={(e) => setName(e.target.value)} />

          <label>Source</label>
          <select value={sourceType} onChange={(e) => setSourceType(e.target.value as SourceType)}>
            <option value="html">Web page — Claude writes the selectors</option>
            <option value="feed">RSS / Atom feed — parsed directly, no AI</option>
            <option value="json">JSON API — read fields by dot-path, no AI</option>
            <option value="csv">CSV / TSV — one row per record, no AI</option>
            <option value="sitemap">Whole site — crawl every page in the sitemap</option>
          </select>

          {sitemapMode && (
            <>
              <label>Sample page</label>
              <input
                placeholder="a representative page URL — Claude designs the extractor on it"
                value={samplePageUrl}
                onChange={(e) => setSamplePageUrl(e.target.value)}
              />
              <label>Max pages</label>
              <input
                type="number"
                min={1}
                max={5000}
                placeholder="optional — pages per run, default 200"
                value={siteMax}
                onChange={(e) => setSiteMax(e.target.value)}
              />
            </>
          )}

          {!feedMode && !sitemapMode && !csvMode && (
            <>
              <label>Shape</label>
              <label className="inline">
                <input type="checkbox" checked={isList} onChange={(e) => setIsList(e.target.checked)} /> a list of items
                (multiple records per page)
              </label>
            </>
          )}
          {jsonMode && (
            <>
              <label>Items path</label>
              <input
                placeholder="optional — dot-path to the array, e.g. data.results"
                value={rootPath}
                onChange={(e) => setRootPath(e.target.value)}
              />
            </>
          )}
          {csvMode && (
            <>
              <label>Delimiter</label>
              <select value={csvDelimiter} onChange={(e) => setCsvDelimiter(e.target.value)}>
                <option value=",">Comma (CSV)</option>
                <option value={"\t"}>Tab (TSV)</option>
                <option value=";">Semicolon</option>
                <option value="|">Pipe</option>
              </select>
              <label>Header row</label>
              <label className="inline">
                <input type="checkbox" checked={csvHasHeader} onChange={(e) => setCsvHasHeader(e.target.checked)} /> the
                first row holds the column names
              </label>
            </>
          )}
          {(feedMode || jsonMode || csvMode) && (
            <>
              <label>Max items</label>
              <input
                type="number"
                min={1}
                max={500}
                placeholder="optional — default 100"
                value={feedMax}
                onChange={(e) => setFeedMax(e.target.value)}
              />
            </>
          )}
        </div>

        {feedMode ? (
          <p className="muted sm mt">
            Feeds have a fixed shape — title, link, summary, published, author, categories. Nothing to configure;
            fetch the feed to preview its items.
          </p>
        ) : csvMode ? (
          <p className="muted sm mt">
            Each row becomes a record keyed by its column headers — nothing to configure. Fetch the CSV to
            preview its rows; the columns are detected automatically.
          </p>
        ) : (
          <>
            <h4 className="mt">
              {jsonMode ? "Fields to extract (by dot-path)" : sitemapMode ? "Fields to extract (per page)" : "Fields to extract"}
            </h4>
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  {jsonMode && <th>Path</th>}
                  <th>Type</th>
                  <th>Required</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {fields.map((f, i) => (
                  <tr key={i}>
                    <td>
                      <input value={f.name} onChange={(e) => patchField(i, { name: e.target.value })} placeholder="e.g. price" />
                    </td>
                    {jsonMode && (
                      <td>
                        <input
                          value={f.path ?? ""}
                          onChange={(e) => patchField(i, { path: e.target.value })}
                          placeholder="e.g. price.amount (defaults to name)"
                        />
                      </td>
                    )}
                    <td>
                      <select value={f.type} onChange={(e) => patchField(i, { type: e.target.value as FieldType })}>
                        {TYPES.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input type="checkbox" checked={f.required} onChange={(e) => patchField(i, { required: e.target.checked })} />
                    </td>
                    <td className="actions">
                      <button className="btn ghost sm" onClick={() => setFields((fs) => fs.filter((_, idx) => idx !== i))} disabled={fields.length === 1}>
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        <div className="row-gap mt">
          {!feedMode && !csvMode && (
            <button className="btn ghost sm" onClick={() => setFields((fs) => [...fs, { name: "", type: "string", required: true }])}>
              + Add field
            </button>
          )}
          {htmlMode && (
            <button className="btn ghost sm" onClick={() => setAdvanced((a) => !a)}>
              {advanced ? "▾ Advanced options" : "▸ Advanced options"}
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button className="btn" onClick={build} disabled={!canBuild}>
            {busy === "assist"
              ? feedMode
                ? "Fetching feed…"
                : jsonMode
                  ? "Fetching JSON…"
                  : csvMode
                    ? "Fetching CSV…"
                    : sitemapMode
                      ? "Designing…"
                      : "Asking Claude…"
              : feedMode
                ? "Fetch feed"
                : jsonMode
                  ? "Fetch JSON"
                  : csvMode
                    ? "Fetch CSV"
                    : sitemapMode
                      ? "Design from sample page"
                      : "✨ Build with AI"}
          </button>
        </div>

        {/* ---- Advanced (collapsed for first-timers; HTML/selector source only) ---- */}
        {advanced && htmlMode && (
          <div className="advanced mt">
            <div className="form-grid">
              <label>Render</label>
              <label className="inline">
                <input type="checkbox" checked={renderJs} onChange={(e) => setRenderJs(e.target.checked)} /> run JavaScript
                (for SPAs — slower)
              </label>

              <label>Interactions</label>
              <div className="actions-editor">
                {actions.length === 0 && (
                  <p className="muted sm" style={{ margin: "0 0 var(--sp2)" }}>
                    Optional steps run on the page (in JS render) before extracting — dismiss a consent
                    banner, fill + submit a search, scroll / load-more.
                  </p>
                )}
                {actions.map((a, i) => (
                  <div key={i} className="action-row">
                    <select value={a.type} onChange={(e) => patchAction(i, { type: e.target.value as ActionType })}>
                      {ACTION_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                    {ACTION_NEEDS_SELECTOR.has(a.type) && (
                      <input
                        placeholder="CSS selector"
                        value={a.selector}
                        onChange={(e) => patchAction(i, { selector: e.target.value })}
                        style={{ flex: 1, minWidth: 120 }}
                      />
                    )}
                    {(a.type === "fill" || a.type === "select") && (
                      <input placeholder="value" value={a.value} onChange={(e) => patchAction(i, { value: e.target.value })} style={{ width: 120 }} />
                    )}
                    {a.type === "press" && (
                      <input placeholder="key (e.g. Enter)" value={a.key} onChange={(e) => patchAction(i, { key: e.target.value })} style={{ width: 120 }} />
                    )}
                    {(a.type === "scroll" || a.type === "loadMore" || a.type === "waitMs") && (
                      <input
                        type="number"
                        placeholder={a.type === "waitMs" ? "ms" : a.type === "scroll" ? "times" : "max"}
                        value={a.num}
                        onChange={(e) => patchAction(i, { num: e.target.value })}
                        style={{ width: 80 }}
                      />
                    )}
                    {(a.type === "waitFor" || a.type === "click") && (
                      <label className="inline muted sm">
                        <input type="checkbox" checked={a.optional} onChange={(e) => patchAction(i, { optional: e.target.checked })} /> optional
                      </label>
                    )}
                    <button type="button" className="btn ghost sm" onClick={() => setActions((xs) => xs.filter((_, idx) => idx !== i))}>
                      ✕
                    </button>
                  </div>
                ))}
                <button type="button" className="btn ghost sm" onClick={() => setActions((xs) => [...xs, newActionRow()])}>
                  + Add step
                </button>
              </div>

              <label>Schedule</label>
              <div>
                <select
                  value={customCron ? "__custom__" : schedule}
                  onChange={(e) => {
                    if (e.target.value === "__custom__") {
                      setCustomCron(true);
                    } else {
                      setCustomCron(false);
                      setSchedule(e.target.value);
                    }
                  }}
                >
                  {SCHEDULES.map((s) => (
                    <option key={s.label} value={s.cron}>
                      {s.label}
                    </option>
                  ))}
                </select>
                {customCron && (
                  <input
                    className="mt"
                    placeholder="cron — e.g. 0 */6 * * *"
                    value={schedule}
                    onChange={(e) => setSchedule(e.target.value)}
                  />
                )}
              </div>

              <label>Webhook</label>
              <input placeholder="optional — POST each result to this URL" value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} />

              <label>Notify</label>
              <label className="inline">
                <input type="checkbox" checked={notifyOnChange} onChange={(e) => setNotifyOnChange(e.target.checked)} /> only POST
                the webhook when the data changed
              </label>

              <label>More URLs</label>
              <textarea
                placeholder="optional — extra target URLs, one per line (the URL above is the AI sample)"
                value={extraUrls}
                onChange={(e) => setExtraUrls(e.target.value)}
                rows={2}
              />

              <label>Folder</label>
              <input
                placeholder="optional — group this scraper (e.g. Competitors)"
                value={folder}
                onChange={(e) => setFolder(e.target.value)}
              />

              <label>Rate limit</label>
              <input
                type="number"
                min="0.1"
                max="20"
                step="0.1"
                placeholder="requests/sec — blank = default politeness"
                value={rps}
                onChange={(e) => setRps(e.target.value)}
              />
            </div>

            {isList && (
              <div className="mt">
                <label className="inline">
                  <input type="checkbox" checked={autopaginate} onChange={(e) => setAutopaginate(e.target.checked)} /> auto-follow
                  pagination — let the AI detect the next-page link on Build
                </label>
                <label className="inline">
                  <input type="checkbox" checked={paginate} onChange={(e) => setPaginate(e.target.checked)} /> paginate — follow
                  the "next page" link (manual / override)
                </label>
                {paginate && (
                  <div className="form-grid mt">
                    <label>Mode</label>
                    <select value={pageMode} onChange={(e) => setPageMode(e.target.value as "selector" | "param")}>
                      <option value="selector">Next-page link (CSS selector)</option>
                      <option value="param">URL page number (?page=N)</option>
                    </select>
                    {pageMode === "selector" ? (
                      <>
                        <label>Next selector</label>
                        <input placeholder="CSS for the next-page link — e.g. li.next a" value={nextSelector} onChange={(e) => setNextSelector(e.target.value)} />
                      </>
                    ) : (
                      <>
                        <label>Page param</label>
                        <input placeholder="URL query param to increment — e.g. page" value={pageParam} onChange={(e) => setPageParam(e.target.value)} />
                      </>
                    )}
                    <label>Max pages</label>
                    <input type="number" min={1} max={50} value={maxPages} onChange={(e) => setMaxPages(Number(e.target.value) || 1)} />
                    <label>Dedupe</label>
                    <label className="inline">
                      <input type="checkbox" checked={pageDedupe} onChange={(e) => setPageDedupe(e.target.checked)} /> drop
                      duplicate items across pages
                    </label>
                  </div>
                )}
              </div>
            )}

            {isList && (
              <div className="mt">
                <label className="inline">
                  <input type="checkbox" checked={detailMode} onChange={(e) => setDetailMode(e.target.checked)} /> list→detail —
                  follow each item to its detail page
                </label>
                {detailMode && (
                  <div className="mt">
                    <div className="form-grid">
                      <label>URL field</label>
                      <input
                        placeholder={'an extracted field holding the detail link (use a "url"-type field above)'}
                        value={detailUrlField}
                        onChange={(e) => setDetailUrlField(e.target.value)}
                      />
                      <label>Max items</label>
                      <input type="number" min={1} max={200} value={detailMaxItems} onChange={(e) => setDetailMaxItems(Number(e.target.value) || 1)} />
                    </div>
                    <table className="mt">
                      <thead>
                        <tr>
                          <th>Detail field</th>
                          <th>Selector (CSS)</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {detailFields.map((f, i) => (
                          <tr key={i}>
                            <td>
                              <input
                                value={f.name}
                                placeholder="e.g. description"
                                onChange={(e) => setDetailFields((fs) => fs.map((x, idx) => (idx === i ? { ...x, name: e.target.value } : x)))}
                              />
                            </td>
                            <td>
                              <input
                                value={f.selector}
                                placeholder="e.g. #product_description + p"
                                onChange={(e) => setDetailFields((fs) => fs.map((x, idx) => (idx === i ? { ...x, selector: e.target.value } : x)))}
                              />
                            </td>
                            <td className="actions">
                              <button className="btn ghost sm" onClick={() => setDetailFields((fs) => fs.filter((_, idx) => idx !== i))} disabled={detailFields.length === 1}>
                                ✕
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <button className="btn ghost sm mt" onClick={() => setDetailFields((fs) => [...fs, { name: "", selector: "" }])}>
                      + Add detail field
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ---- Authentication (advanced) — per-scraper request auth for ALL source types: read auth-gated
             pages + reuse a human-solved cf_clearance cookie. Applied to saved/scheduled runs. Secrets are
             sent same-host only and are NEVER included when the scraper is exported or published. ---- */}
        <div className="row-gap mt">
          <button type="button" className="btn ghost sm" onClick={() => setAuthOpen((a) => !a)}>
            {authOpen ? "▾ Authentication" : "▸ Authentication"}
          </button>
          {!authOpen && (authCookies.trim() !== "" || authHeaders.some((h) => h.key.trim() !== "")) && (
            <span className="muted sm">· auth set</span>
          )}
        </div>
        {authOpen && (
          <div className="advanced mt">
            <p className="muted sm" style={{ marginTop: 0 }}>
              Optional. Send extra headers (e.g. <span className="mono">Authorization</span> / an API key)
              and/or cookies with every fetch (this preview + scheduled runs) — to scrape pages that need a
              login, or to reuse a <span className="mono">cf_clearance</span> cookie you solved once in your
              own browser. Sent only to the target's own origin; <b>never</b> included when you export or
              publish this scraper.
            </p>
            <div className="form-grid">
              <label>Cookies</label>
              <textarea
                placeholder="raw Cookie header — e.g. sessionid=abc; cf_clearance=xyz"
                value={authCookies}
                onChange={(e) => setAuthCookies(e.target.value)}
                rows={2}
              />
              <label>Headers</label>
              <div className="actions-editor">
                {authHeaders.length === 0 && (
                  <p className="muted sm" style={{ margin: "0 0 var(--sp2)" }}>
                    No custom headers.
                  </p>
                )}
                {authHeaders.map((h, i) => (
                  <div key={i} className="action-row">
                    <input
                      placeholder="header — e.g. Authorization"
                      value={h.key}
                      onChange={(e) =>
                        setAuthHeaders((xs) => xs.map((x, idx) => (idx === i ? { ...x, key: e.target.value } : x)))
                      }
                      style={{ flex: 1, minWidth: 140 }}
                    />
                    <input
                      placeholder="value — e.g. Bearer …"
                      value={h.value}
                      onChange={(e) =>
                        setAuthHeaders((xs) => xs.map((x, idx) => (idx === i ? { ...x, value: e.target.value } : x)))
                      }
                      style={{ flex: 1, minWidth: 140 }}
                    />
                    <button
                      type="button"
                      className="btn ghost sm"
                      onClick={() => setAuthHeaders((xs) => xs.filter((_, idx) => idx !== i))}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn ghost sm"
                  onClick={() => setAuthHeaders((xs) => [...xs, { key: "", value: "" }])}
                >
                  + Add header
                </button>
              </div>
            </div>
          </div>
        )}

        {error && <div className="err mt">{error}</div>}
      </section>

      {/* ---- Step 2a: assist sniffed a non-HTML body — offer to switch source type ---- */}
      {result?.detected_source && (
        <section className="card">
          <div className="step-row">
            <span className="step-n">2</span>
            <h3>Different source detected</h3>
          </div>
          <p className="mt">
            {result.message ??
              `This URL looks like ${
                result.detected_source === "json"
                  ? "a JSON API"
                  : result.detected_source === "csv"
                    ? "a CSV / TSV file"
                    : "an RSS/Atom feed"
              }.`}
          </p>
          <div className="row-gap mt">
            <button
              className="btn"
              onClick={() => {
                if (result.detected_source) setSourceType(result.detected_source);
                setResult(null);
              }}
            >
              Switch to{" "}
              {result.detected_source === "json"
                ? "JSON API"
                : result.detected_source === "csv"
                  ? "CSV / TSV"
                  : "RSS / Atom feed"}
            </button>
          </div>
        </section>
      )}

      {/* ---- Step 2: the AI's result ---- */}
      {result && !result.detected_source && (
        <section className="card">
          <div className="step-row">
            <span className="step-n">2</span>
            <h3>
              Preview{" "}
              <span className={`pill ${result.valid ? "success" : "failed"}`}>{result.valid ? "looks good" : "needs work"}</span>
            </h3>
            <div style={{ flex: 1 }} />
            <button className="btn" onClick={save} disabled={busy !== null || !result.valid}>
              {busy === "save" ? "Saving…" : "Save & run →"}
            </button>
          </div>
          {notice && (
            <div className="mt" style={{ fontSize: "var(--t-cap)", color: "var(--gold, #9a7b3f)" }}>
              {notice}
            </div>
          )}
          <div className="stat-row mt">
            <span>
              items <b>{result.itemCount}</b>
            </span>
            <span>
              null-rate <b>{Math.round(result.nullRate * 100)}%</b>
            </span>
            {result.usage ? (
              <span className="muted">
                {result.usage.model} · {result.usage.inputTokens}+{result.usage.outputTokens} tok
              </span>
            ) : (
              <span className="muted">parsed directly · no AI</span>
            )}
          </div>
          {isList &&
            autopaginate &&
            (() => {
              const pag = (result.extraction_config as { pagination?: { nextSelector: string; maxPages: number } })
                .pagination;
              return pag ? (
                <div className="note mt">
                  ✓ next-page link detected: <span className="mono">{pag.nextSelector}</span> · will follow up to{" "}
                  {pag.maxPages} pages — edit or clear under <em>Advanced → paginate</em>.
                </div>
              ) : (
                <div className="muted sm mt">No pagination found — single page.</div>
              );
            })()}
          {result.fieldErrors.length > 0 && (
            <ul className="err small">
              {result.fieldErrors.map((fe, i) => (
                <li key={i}>{fe}</li>
              ))}
            </ul>
          )}
          <div className="mt">
            <DataTable rows={result.preview} limit={10} />
          </div>
          <button className="link-btn" onClick={() => setShowRaw((s) => !s)}>
            {showRaw ? "Hide raw JSON" : "View raw JSON"}
          </button>
          {showRaw && <pre className="preview">{JSON.stringify(result.preview, null, 2)}</pre>}

          {/* ---- Operator (N6): publish this AI-built config straight to the Templates gallery ---- */}
          {operator && result.valid && (
            <div
              className="mt"
              style={{ borderTop: "1px solid var(--rule)", paddingTop: "var(--sp4)", marginTop: "var(--sp5)" }}
            >
              <h4 style={{ margin: "0 0 4px" }}>Operator · publish as template</h4>
              <p className="muted sm" style={{ marginTop: 0 }}>
                Add this config to the shared <b>Templates</b> gallery — no throwaway scraper. Any workspace
                can then instantiate it in one click.
              </p>
              {!pubOpen && (
                <button
                  className="btn ghost sm"
                  onClick={() => {
                    setPubOpen(true);
                    setTplName(name.trim() || url.trim());
                  }}
                >
                  Publish as template…
                </button>
              )}
              {pubOpen && (
                <div className="form-grid mt">
                  <label htmlFor="tpl-name">Template name</label>
                  <input id="tpl-name" value={tplName} onChange={(e) => setTplName(e.target.value)} />
                  <label htmlFor="tpl-desc">Description</label>
                  <input
                    id="tpl-desc"
                    placeholder="optional — what this template scrapes"
                    value={tplDesc}
                    onChange={(e) => setTplDesc(e.target.value)}
                  />
                  <div />
                  <label className="inline">
                    <input
                      type="checkbox"
                      checked={testInstance}
                      onChange={(e) => setTestInstance(e.target.checked)}
                    />{" "}
                    also create a test instance in my workspace (for heal testing)
                  </label>
                  <div />
                  <div className="row-gap">
                    <button className="btn sm" onClick={publishTemplate} disabled={busy !== null || !tplName.trim()}>
                      {busy === "save" ? "Publishing…" : "Publish template"}
                    </button>
                    <button className="btn ghost sm" onClick={() => setPubOpen(false)} disabled={busy !== null}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
